import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Trophy, Star, Award } from 'lucide-react';
import { formatApiError } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const slotColor = { GK: 'bg-amber-500/20 text-amber-300 border-amber-400/30', DEF: 'bg-sky-500/20 text-sky-300 border-sky-400/30', ATA: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30' };

const LineupDetailModal = ({ matchDayId, userId, userName, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!matchDayId || !userId) return;
    (async () => {
      setLoading(true); setErr('');
      try {
        const { data } = await axios.get(`${API}/fantasy/lineup-detail`, { params: { match_day_id: matchDayId, user_id: userId } });
        setData(data);
      } catch (ex) { setErr(formatApiError(ex)); } finally { setLoading(false); }
    })();
  }, [matchDayId, userId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-start sm:items-center justify-center p-3 overflow-y-auto" onClick={onClose} data-testid="lineup-detail-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl max-w-2xl w-full my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
          <div className="min-w-0">
            <div className="text-white font-black truncate">{userName || data?.user_name}</div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-300/80">Escalação da jornada</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-slate-300" data-testid="lineup-detail-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">
          {loading ? <div className="text-slate-300 text-sm">A carregar...</div>
            : err ? <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{err}</div>
            : data ? (
              <>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-slate-800/60 border border-white/10 rounded-xl p-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-400">Total da Jornada</div>
                    <div className="text-emerald-300 font-black text-2xl tabular-nums flex items-center gap-1"><Award className="w-5 h-5" /> {data.total_points.toFixed(1)}</div>
                  </div>
                  <div className="bg-slate-800/60 border border-white/10 rounded-xl p-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-400">Custo da Equipa</div>
                    <div className="text-white font-black text-2xl tabular-nums">{data.total_cost.toFixed(1)} <span className="text-sm text-slate-400">€M</span></div>
                  </div>
                </div>
                <div className="space-y-2">
                  {data.slots.map((s) => (
                    <div key={s.profile_id} className="flex items-center gap-3 bg-slate-800/40 border border-white/5 rounded-xl p-3" data-testid={`slot-${s.slot}-${s.profile_id}`}>
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-black tracking-widest border ${slotColor[s.slot]}`}>{s.slot}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 text-white font-bold truncate">
                          {s.is_captain && <Star className="w-4 h-4 text-amber-300 fill-amber-300" />}
                          {s.player_name}
                          <span className="text-slate-500 text-xs font-normal ml-1">· {s.rating}</span>
                        </div>
                        <div className="text-[11px] text-slate-400 mt-0.5 flex flex-wrap gap-x-3">
                          <span>Base <b className="text-slate-200">{s.raw_points.toFixed(1)}</b></span>
                          {s.position_bonus > 0 && <span className="text-emerald-300">+bónus {s.position_bonus.toFixed(1)}</span>}
                          {s.is_captain && <span className="text-amber-300">×{s.captain_multiplier}</span>}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-emerald-300 font-black text-lg tabular-nums">{s.total.toFixed(1)}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-400">
                  <Trophy className="w-3.5 h-3.5 text-amber-300" />
                  <span>Bónus de posição = jogador escalado no slot que corresponde à posição real (+1.0).</span>
                </div>
              </>
            ) : null}
        </div>
      </div>
    </div>
  );
};

export default LineupDetailModal;
